// ink-component
import React, { ReactNode, MouseEvent, ChangeEvent, KeyboardEvent, InputHTMLAttributes } from 'react';
import { ThemeConsumer } from 'styled-components';

import CoreTypeahead from '../_common/CoreTypeahead/CoreTypeahead';
import TableTypeahead from './components/TableTypeahead/TableTypeahead';

import { NewInputProps } from '../NewInput/NewInput';

export interface NewTypeaheadProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /** Unique identifier */
  id: string;
  /** Externally controlled value */
  value: string;
  /** onChange callback, sends the event and the value and
   * selected option, when it exists */
  onChange: (
    e: MouseEvent<HTMLButtonElement> | ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>,
    { value, option }: { value: string; option: any },
  ) => void;
  /** Array of options to be rendered */
  options: any[];
  /** Indicate that the Typeahead is fetching options asynchronously */
  loading?: boolean;
  /** Function to get the value to be shown */
  optionLabel: (option: any) => string;
  /** Function to filter the options when the users changes the text */
  filterOptions?: (value: string) => any[];
  /** Render prop with an input component */
  input: (inputProps: NewInputProps) => ReactNode;
  /** Render prop to change the visual of the options */
  option?: (props: any) => JSX.Element;
  /** The typeahead won't show a "no match" message if the users types something that's not on the options list */
  allowCustom?: boolean;
  /** Message displayed in the menu when there are no valid results */
  emptyMessage?: ReactNode | string;
  /** Keep the item opened even after you select it */
  keepOpen?: boolean;
  /** Make the dropdown list scrollable (recommended for a long list of items) */
  scrollable?: boolean;
  /** Width of the input */
  width?: 'large' | 'half' | 'quarter' | 'full';
  /** Width of the dropdown */
  dropdownWidth?: string;
  /** Max height of the dropdown */
  dropdownMaxHeight?: React.CSSProperties['maxHeight'];
  /** Allows screen readers to describe the content. The correct use of Field excludes the need for aria-label */
  'aria-label'?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;

  /** @ignore */
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  /** @ignore */
  portalContainer?: React.RefObject<HTMLDivElement>;
}

/**
 * @purpose Search and select an option among matching results
 * @categories form
 * @aka typeahead, search
 * @HTMLType HTMLInputElement
 */
function NewTypeahead(props: NewTypeaheadProps) {
  const { className, style, ...otherProps } = props;

  return (
    <ThemeConsumer>
      {cellTheme => {
        if (cellTheme && cellTheme.cellPreset === 'editable') {
          return <TableTypeahead {...otherProps} />;
        }
        return <CoreTypeahead {...otherProps} />;
      }}
    </ThemeConsumer>
  );
}

NewTypeahead.defaultProps = {
  option: undefined,
  loading: undefined,
  allowCustom: false,
  emptyMessage: undefined,
  keepOpen: false,
  scrollable: false,
  width: 'large',
};

NewTypeahead.displayName = 'NewTypeahead';

export default NewTypeahead;
